import { MutableData } from '@polymer/polymer/lib/mixins/mutable-data.js';
import { html } from '../../../mixins/katapult-element.js';
import { KatapultModelEditorElement } from '../katapult-editor-template.js';
import { PortalStyle } from '../katapult-model-editor-style.js';
import '@polymer/paper-menu-button/paper-menu-button.js';
import '../../paper-header/paper-header.js';
import '../../paper-table/paper-table.js';
import '../../material-icon/material-icon.js';
import '../../katapult-drop-down/katapult-drop-down.js';
import '../../style-modules/paper-table-style.js';

import './workflow-action-editor.js';

customElements.define(
  'workflow-action-list',
  class extends MutableData(KatapultModelEditorElement) {
    static get template() {
      return html`
        <!-- Style -->
        ${PortalStyle}
        <style>
          .manage-entry-action-button {
            --paper-menu-button-content: {
              border-radius: 16px;
              white-space: nowrap;
            };
          }
        </style>
        <!-- Table -->
        <paper-table rounded white outline dividers grow overflow-hidden>
          <paper-table-scroll data-path="actions" on-sort-changed="orderedItemsArraySortChanged">
            <!-- Header -->
            <paper-row grey short slot="before">
              <paper-cell icon>
                <material-icon icon="swap_vert"></material-icon>
              </paper-cell>
              <paper-cell>Action</paper-cell>
              <paper-cell icon-button>
                <paper-menu-button class="manage-entry-action-button" horizontal-align="right" vertical-align="top" vertical-offset="48">
                  <katapult-button iconOnly noBorder slot="dropdown-trigger" icon-button icon="more_vert"></katapult-button>
                  <div slot="dropdown-content" style="border-radius: 12px; cursor: pointer;">
                    <div
                      flex
                      align-center
                      style="padding: 12px; gap: 8px; position: relative;"
                      data-copy-type="ENTRY_ACTION"
                      on-click="promptPaste"
                    >
                      <paper-ripple></paper-ripple>
                      <material-icon icon="content_paste"></material-icon>
                      <div>Paste</div>
                    </div>
                  </div>
                </paper-menu-button>
              </paper-cell>
            </paper-row>
            <!-- Body -->
            <template is="dom-repeat" items="[[getOrderedItemsArray(actions.*)]]" mutable-data>
              <paper-header-content
                data-path="actions.$(model.item.$key)"
                on-opened-changed="updateProperty"
                on-render-changed="updateProperty"
                slot="sortable"
                opened="[[item.$opened]]"
                no-expand="[[includes(actionsWithNoEditor, item.action)]]"
                divider
                no-click-row
              >
                <paper-cell slot="header" icon drag-handle>[[add(index, 1)]]</paper-cell>
                <paper-cell slot="header">
                  <katapult-drop-down
                    label="Action"
                    value="[[item.action]]"
                    value-path="value"
                    label-path="label"
                    secondary-label-path="description"
                    items="[[actionOptions]]"
                    no-label-float
                    data-path="actions.$(model.item.$key).action"
                    on-selected-changed="updateProperty"
                  ></katapult-drop-down>
                </paper-cell>
                <paper-cell slot="header">
                  <div>[[_findActionDescription(item.action)]]</div>
                </paper-cell>
                <paper-cell slot="header-end" icon-button>
                  <paper-menu-button class="manage-entry-action-button" horizontal-align="right" vertical-align="top" vertical-offset="48">
                    <katapult-button iconOnly noBorder slot="dropdown-trigger" icon-button icon="more_vert"></katapult-button>
                    <div slot="dropdown-content" style="border-radius: 12px; cursor: pointer;">
                      <div
                        flex
                        align-center
                        style="padding: 12px; gap: 8px; position: relative;"
                        data-copy-type="ENTRY_ACTION"
                        on-click="promptCopy"
                      >
                        <paper-ripple></paper-ripple>
                        <material-icon icon="content_copy"></material-icon>
                        <div>Copy</div>
                      </div>
                      <div
                        flex
                        align-center
                        style="padding: 12px; gap: 8px; position: relative;"
                        data-path="actions.$(model.item.$key)"
                        data-value="$null"
                        on-click="updateProperty"
                      >
                        <paper-ripple></paper-ripple>
                        <material-icon icon="delete"></material-icon>
                        <div>Delete</div>
                      </div>
                    </div>
                  </paper-menu-button>
                </paper-cell>
                <template is="dom-if" if="[[item.$render]]" restamp>
                  <div slot="content">
                    <workflow-action-editor
                      item="[[item]]"
                      app-type="[[appType]]"
                      export-options="[[exportOptions]]"
                      timeline-models="[[timelineModels]]"
                      other-attributes="[[otherAttributes]]"
                      user-group="[[userGroup]]"
                      data-path="actions.$(model.item.$key)"
                      on-item-changed="updateProperty"
                      model-space-details="[[modelSpaceDetails]]"
                      companies="[[companies]]"
                    ></workflow-action-editor>
                  </div>
                </template>
              </paper-header-content>
            </template>
            <!-- Add Button -->
            <paper-row class="addButton" ripple short slot="after" data-path="actions" on-click="addOrderedItem">
              <paper-cell>
                <material-icon icon="bolt"></material-icon>
                <div>Add an Action</div>
              </paper-cell>
            </paper-row>
          </paper-table-scroll>
        </paper-table>
      `;
    }

    constructor() {
      super();
      this.actionsWithNoEditor = [
        'SET_ENGINEERING_CONTRACTOR',
        'SET_DATA_COLLECTION_CONTRACTOR',
        'SET_REVIEW_CONTRACTOR',
        'UPDATE_POLES_ENTRY',
        'UPDATE_APP_IN_PORTAL_DIRECTORIES'
      ];
      this.actionOptions = [
        { label: 'Set Status', value: 'SET_STATUS', description: 'Change the status of the application' },
        { label: 'Set Metadata', value: 'SET_METADATA', description: 'Set a job metadata attribute on the application' },
        { label: 'Set Timer', value: 'SET_TIMER', description: 'Set a timer on the application that can trigger additional actions' },
        {
          label: 'Change Timer Length',
          value: 'SET_TIMER_LENGTH',
          description: 'Adjust the length of an existing timer on the application'
        },
        {
          label: 'Clear Timer',
          value: 'CLEAR_TIMER',
          description: 'Clear a timer on the application (timers are cleared automatically on status changes)'
        },
        {
          label: 'Set Unique ID',
          value: 'SET_APP_NUMBER',
          description: 'Sets the name of the job to a unique identifier using the selected method.'
        },
        {
          label: 'Auto-Assign Engineering Contractor',
          value: 'SET_ENGINEERING_CONTRACTOR',
          description: 'Assign one or more engineering contractors to the application, per the assignment rules from "User Options"'
        },
        {
          label: 'Auto-Assign Data Collection Contractor',
          value: 'SET_DATA_COLLECTION_CONTRACTOR',
          description: 'Assign one or more data collection contractors to the application, per the assignment rules from "User Options"'
        },
        {
          label: 'Auto-Assign Review Contractor',
          value: 'SET_REVIEW_CONTRACTOR',
          description: 'Assign one or more review contractors to the application, per the assignment rules from "User Options"'
        },
        { label: 'Set Timeline', value: 'SET_TIMELINE', description: 'Set a timeline on the application' },
        {
          label: 'Update Submission Record',
          value: 'UPDATE_POLES_ENTRY',
          description: "Manually update this app in the active apps list and its pole count in the attacher's submission window"
        },
        {
          label: 'Update App in Portal Directories (MLDs)',
          value: 'UPDATE_APP_IN_PORTAL_DIRECTORIES',
          description:
            "Check this app's poles in to all companies' portal directories, and add or remove the poles from the collaboration directory"
        },
        { label: 'Generate Export', value: 'GENERATE_EXPORT', description: 'Run a custom data export' },
        { label: 'Send Email', value: 'SEND_EMAIL', description: 'Send an email to one or more portal users' },
        {
          label: 'Share Application',
          value: 'SHARE_APP',
          description: 'Add or change sharing permissions on the application for one or more companies'
        }
      ];
    }

    static get properties() {
      return {
        actionOptions: { type: Array },
        actions: { type: Object, notify: true },
        actionsWithNoEditor: { type: Array },
        appType: Object,
        exportOptions: Object,
        otherAttributes: Object,
        companies: { type: Object },
        timelineModels: { type: Object },
        userGroup: { type: String }
      };
    }

    _findActionDescription(action) {
      return this.actionOptions.find((option) => option.value === action)?.description ?? '';
    }

    promptCopy(e) {
      e.currentTarget.closest('paper-menu-button')?.close();
      this.dispatchEvent(new CustomEvent('copy', { detail: { actionId: e.model.item.$key, copyType: e.currentTarget.dataset.copyType } }));
    }

    promptPaste(e) {
      e.currentTarget.closest('paper-menu-button')?.close();
      this.dispatchEvent(new CustomEvent('paste'));
    }
  }
);
